import { Button, Card } from "@ui/shad"
import { cn } from "@ui/utils"
import { ArrowRight, Check, FolderPlus } from "lucide-react"

import { useActionLink, useExecuteActionLink } from "@/hooks/api/action-links"
import { Link } from "@/components/book-link"
import { useCreateProjectDialog } from "@/components/projects/create-project-dialog"

interface StartProjectProps {
  id: string
}

export default function StartProject({ id }: StartProjectProps) {
  const { data: actionLink } = useActionLink(id)
  const { mutate: execute, isPending, isSuccess } = useExecuteActionLink()
  const createProjectDialog = useCreateProjectDialog()

  if (!actionLink) return <>{createProjectDialog.dialog}</>

  const templateTitle =
    typeof actionLink.properties?.project_template_title === "string"
      ? actionLink.properties.project_template_title
      : "Project"
  const householdName =
    typeof actionLink.properties?.household_name === "string" ? actionLink.properties.household_name : undefined
  const assignedToName =
    typeof actionLink.properties?.assigned_to_name === "string" ? actionLink.properties.assigned_to_name : undefined
  const assignedTeamName =
    typeof actionLink.properties?.assigned_team_name === "string" ? actionLink.properties.assigned_team_name : undefined
  const createdProjectId =
    typeof actionLink.properties?.created_project_id === "string" ? actionLink.properties.created_project_id : undefined
  const requiresFormInput = actionLink.properties?.requires_form_input === true

  const isExpired = new Date(actionLink.expires_at) < new Date()
  const isExecuted = !!actionLink.executed_at || isSuccess
  const isDisabled = isPending || isExpired || isExecuted

  const label = (() => {
    if (isPending) return "Starting..."
    if (isExecuted) return "Project started"
    if (isExpired) return "Expired"
    return requiresFormInput ? "Complete project setup" : "Start project"
  })()

  const buttonVariant = isExecuted || isExpired ? "secondary" : "outline"

  const buttonClassName = cn(
    "transition-all",
    isExecuted && "cursor-default border-green-border bg-green-background text-green-foreground hover:bg-green-border",
    isExpired && "cursor-default border-tertiary bg-tinted text-muted-foreground hover:bg-tinted"
  )

  const subtitle = [
    householdName,
    assignedToName || assignedTeamName ? `Assigned to ${assignedToName ?? assignedTeamName}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <>
      {createProjectDialog.dialog}
      <Card className="mt-2 flex w-full max-w-md flex-col gap-2 p-4 shadow-sm">
        <div>
          <h3 className="text-base font-medium">{templateTitle}</h3>
          {subtitle && <p className="text-muted-foreground text-xs">{subtitle}</p>}
        </div>

        <Button
          variant={buttonVariant}
          onClick={() => {
            if (isDisabled) return

            if (requiresFormInput) {
              createProjectDialog.open({
                templateId:
                  typeof actionLink.properties?.project_template_sqid === "string"
                    ? actionLink.properties.project_template_sqid
                    : undefined,
                householdId:
                  typeof actionLink.properties?.household_sqid === "string"
                    ? actionLink.properties.household_sqid
                    : undefined,
                bookId:
                  typeof actionLink.properties?.book_sqid === "string" ? actionLink.properties.book_sqid : undefined,
                assignedToId:
                  typeof actionLink.properties?.assigned_to_sqid === "string"
                    ? actionLink.properties.assigned_to_sqid
                    : undefined,
                assignedTeamId:
                  typeof actionLink.properties?.assigned_team_sqid === "string"
                    ? actionLink.properties.assigned_team_sqid
                    : undefined,
                projectName:
                  typeof actionLink.properties?.project_name === "string"
                    ? actionLink.properties.project_name
                    : undefined,
                initialFormResponses: actionLink.properties?.form_responses,
                actionLinkId: id,
                lockTemplate: true,
              })
            } else {
              execute({ id })
            }
          }}
          disabled={isDisabled}
          className={buttonClassName}
          size="sm"
        >
          {isExecuted ? <Check /> : <FolderPlus />}
          {label}
        </Button>

        {isExecuted && createdProjectId && (
          <Link
            href={`/projects/${createdProjectId}`}
            className="text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 text-xs"
          >
            View project
            <ArrowRight className="size-3" />
          </Link>
        )}
      </Card>
    </>
  )
}
